import React, { useState, useEffect } from 'react';
import { Settings, Zap, Target, TrendingUp, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { Select } from '../../ui/Select';

interface OptimizationResult {
  skuId: string;
  skuName: string;
  currentStock: number;
  optimalStock: number;
  adjustment: number;
  reason: string;
  impact: 'high' | 'medium' | 'low';
  savings: number;
}

interface OptimizationScenario {
  name: string;
  description: string;
  targetMetric: string;
  expectedImprovement: number;
  results: OptimizationResult[];
}

const OptimizationEngine: React.FC = () => {
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState<'cost' | 'service' | 'balanced'>('balanced');
  const [optimizationResults, setOptimizationResults] = useState<OptimizationResult[]>([]);
  const [scenarios, setScenarios] = useState<OptimizationScenario[]>([]);
  const [activeScenario, setActiveScenario] = useState<number>(0);
  
  // 最適化パラメータ
  const [parameters, setParameters] = useState({
    targetServiceLevel: 95,
    maxStockDays: 30,
    minStockDays: 7,
    safetyStockMultiplier: 1.5,
    seasonalAdjustment: true,
    demandVolatility: 'medium'
  });
  
  // 最適化結果を生成
  const generateOptimizationResults = () => {
    const results: OptimizationResult[] = [
      {
        skuId: 'SKU001',
        skuName: 'コカ・コーラ 500ml',
        currentStock: 850,
        optimalStock: 720,
        adjustment: -130,
        reason: '需要安定、過剰在庫削減可能',
        impact: 'high',
        savings: 18200
      },
      {
        skuId: 'SKU002',
        skuName: '牛乳 1L',
        currentStock: 320,
        optimalStock: 380,
        adjustment: 60,
        reason: '週末需要増、欠品リスク回避',
        impact: 'medium',
        savings: -4200
      },
      {
        skuId: 'SKU003',
        skuName: 'ポテトチップス うすしお',
        currentStock: 450,
        optimalStock: 400,
        adjustment: -50,
        reason: '回転率向上、鮮度管理改善',
        impact: 'low',
        savings: 5000
      },
      {
        skuId: 'SKU004',
        skuName: 'サントリー天然水 2L',
        currentStock: 680,
        optimalStock: 600,
        adjustment: -80,
        reason: 'リードタイム短縮により削減可能',
        impact: 'medium',
        savings: 8000
      },
      {
        skuId: 'SKU005',
        skuName: '食パン 6枚切',
        currentStock: 180,
        optimalStock: 220,
        adjustment: 40,
        reason: '朝の需要ピーク対応',
        impact: 'high',
        savings: -2000
      },
      {
        skuId: 'SKU006',
        skuName: 'ヨーグルト 4個パック',
        currentStock: 250,
        optimalStock: 250,
        adjustment: 0,
        reason: '現在の在庫レベルが最適',
        impact: 'low',
        savings: 0
      },
      {
        skuId: 'SKU007',
        skuName: '冷凍餃子 12個入',
        currentStock: 380,
        optimalStock: 320,
        adjustment: -60,
        reason: '保管コスト削減',
        impact: 'medium',
        savings: 7200
      },
      {
        skuId: 'SKU008',
        skuName: 'バナナ 1房',
        currentStock: 120,
        optimalStock: 150,
        adjustment: 30,
        reason: '高回転商品、充足率向上',
        impact: 'high',
        savings: -1500
      }
    ];
    
    // 戦略に応じて結果を調整
    if (selectedStrategy === 'cost') {
      results.forEach(r => {
        if (r.adjustment > 0) {
          r.adjustment = Math.floor(r.adjustment * 0.7);
          r.optimalStock = r.currentStock + r.adjustment;
        }
      });
    } else if (selectedStrategy === 'service') {
      results.forEach(r => {
        if (r.adjustment < 0) {
          r.adjustment = Math.floor(r.adjustment * 0.5);
          r.optimalStock = r.currentStock + r.adjustment;
        }
      });
    }
    
    setOptimizationResults(results);
    
    // シナリオを生成
    const newScenarios: OptimizationScenario[] = [
      {
        name: '基本最適化',
        description: '現在の需要予測に基づく標準的な最適化',
        targetMetric: '在庫回転率',
        expectedImprovement: 12,
        results: results
      },
      {
        name: 'コスト重視',
        description: '保管コストと廃棄ロスの最小化',
        targetMetric: '在庫保有コスト',
        expectedImprovement: 18,
        results: results.map(r => ({ ...r, adjustment: r.adjustment < 0 ? r.adjustment * 1.2 : r.adjustment * 0.8 }))
      },
      {
        name: 'サービス重視',
        description: '欠品率を最小限に抑える',
        targetMetric: '充足率',
        expectedImprovement: 8,
        results: results.map(r => ({ ...r, adjustment: r.adjustment > 0 ? r.adjustment * 1.3 : r.adjustment * 0.7 }))
      }
    ];
    
    setScenarios(newScenarios);
  };
  
  const runOptimization = () => {
    setIsOptimizing(true);
    setTimeout(() => {
      generateOptimizationResults();
      setIsOptimizing(false);
      
      const totalSavings = optimizationResults.reduce((sum, r) => sum + r.savings, 0);
      const adjustmentCount = optimizationResults.filter(r => r.adjustment !== 0).length;
      const avgImprovement = 15 + Math.random() * 10;
      
      alert(`✅ 在庫最適化を実行しました！\n\n最適化結果:\n- 最適化対象SKU: ${adjustmentCount}件\n- 削減可能金額: ¥${totalSavings.toLocaleString()}\n- 在庫効率改善: ${avgImprovement.toFixed(1)}%\n- 欠品リスク削減: ${(5 + Math.random() * 5).toFixed(1)}%\n\n戦略: ${selectedStrategy === 'cost' ? 'コスト重視' : selectedStrategy === 'service' ? 'サービス重視' : 'バランス型'}`);
    }, 2000);
  };
  
  useEffect(() => {
    generateOptimizationResults();
  }, [selectedStrategy]);
  
  const getImpactColor = (impact: 'high' | 'medium' | 'low') => {
    switch (impact) {
      case 'high': return colors.accent.red;
      case 'medium': return colors.accent.orange;
      case 'low': return colors.text.secondary;
    }
  };
  
  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold mb-1" style={{ color: colors.text.primary }}>
              在庫最適化エンジン
            </h3>
            <p className="text-sm" style={{ color: colors.text.secondary }}>
              AIによる在庫レベルの自動最適化
            </p>
          </div>
          <div className="flex gap-2">
            <Select
              value={selectedStrategy}
              onChange={(value) => setSelectedStrategy(value as any)}
              options={[
                { value: 'balanced', label: 'バランス型' },
                { value: 'cost', label: 'コスト重視' },
                { value: 'service', label: 'サービス重視' }
              ]}
            />
            <Button 
              variant="primary"
              onClick={runOptimization}
              disabled={isOptimizing}
            >
              <Zap size={16} className={isOptimizing ? 'animate-pulse' : ''} />
              {isOptimizing ? '最適化中...' : '最適化実行'}
            </Button>
          </div>
        </div>
        
        {/* 最適化パラメータ */}
        <div className="grid grid-cols-3 gap-4">
          <div className="p-3 rounded-lg" style={{ backgroundColor: colors.ui.bgSecondary }}>
            <div className="flex items-center gap-2 mb-2">
              <Target size={16} style={{ color: colors.accent.blue }} />
              <span className="text-sm font-medium" style={{ color: colors.text.primary }}>
                目標サービスレベル
              </span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="85"
                max="99"
                value={parameters.targetServiceLevel}
                onChange={(e) => setParameters({ ...parameters, targetServiceLevel: parseInt(e.target.value) })}
                className="flex-1"
              />
              <span className="text-sm font-bold" style={{ color: colors.accent.blue }}>
                {parameters.targetServiceLevel}%
              </span>
            </div>
          </div>
          
          <div className="p-3 rounded-lg" style={{ backgroundColor: colors.ui.bgSecondary }}>
            <div className="flex items-center gap-2 mb-2">
              <Settings size={16} style={{ color: colors.accent.green }} />
              <span className="text-sm font-medium" style={{ color: colors.text.primary }}>
                安全在庫係数
              </span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="1"
                max="3"
                step="0.1"
                value={parameters.safetyStockMultiplier}
                onChange={(e) => setParameters({ ...parameters, safetyStockMultiplier: parseFloat(e.target.value) })}
                className="flex-1"
              />
              <span className="text-sm font-bold" style={{ color: colors.accent.green }}>
                {parameters.safetyStockMultiplier.toFixed(1)}x
              </span>
            </div>
          </div>
          
          <div className="p-3 rounded-lg" style={{ backgroundColor: colors.ui.bgSecondary }}>
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={16} style={{ color: colors.accent.purple }} />
              <span className="text-sm font-medium" style={{ color: colors.text.primary }}>
                最大在庫日数
              </span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="14"
                max="60"
                value={parameters.maxStockDays}
                onChange={(e) => setParameters({ ...parameters, maxStockDays: parseInt(e.target.value) })}
                className="flex-1"
              />
              <span className="text-sm font-bold" style={{ color: colors.accent.purple }}>
                {parameters.maxStockDays}日
              </span>
            </div>
          </div>
        </div>
      </Card>
      
      {/* シナリオタブ - コンパクト版 */}
      {scenarios.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {scenarios.map((scenario, index) => (
            <Card 
              key={index}
              className={`p-2 cursor-pointer border-2 transition-all ${
                activeScenario === index ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
              }`}
              onClick={() => setActiveScenario(index)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h5 className="text-xs font-semibold mb-0.5" style={{ color: colors.text.primary }}>
                    {scenario.name}
                  </h5>
                  <p className="text-[10px]" style={{ color: colors.text.secondary }}>
                    {scenario.description}
                  </p>
                </div>
                <div className="text-right ml-2">
                  <div className="text-sm font-bold" style={{ color: colors.accent.green }}>
                    +{scenario.expectedImprovement}%
                  </div>
                  <div className="text-[9px]" style={{ color: colors.text.secondary }}>
                    {scenario.targetMetric}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      
      {/* 最適化結果 */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium" style={{ color: colors.text.primary }}>
            最適化提案 ({optimizationResults.length}件)
          </h4>
          <div className="flex items-center gap-2">
            <Badge variant="success">
              削減可能: ¥{optimizationResults.reduce((sum, r) => sum + Math.max(0, r.savings), 0).toLocaleString()}
            </Badge>
            <Button variant="secondary" size="sm">
              提案を適用
            </Button>
          </div>
        </div>
        
        {isOptimizing ? (
          <div className="h-64 flex items-center justify-center">
            <div className="text-center">
              <RefreshCw size={48} className="animate-spin mx-auto mb-2" style={{ color: colors.accent.blue }} />
              <p style={{ color: colors.text.secondary }}>最適化処理中...</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: colors.ui.border }}>
                  <th className="text-left py-2 px-3 text-sm font-medium" style={{ color: colors.text.secondary }}>
                    SKU
                  </th>
                  <th className="text-left py-2 px-3 text-sm font-medium" style={{ color: colors.text.secondary }}>
                    商品名
                  </th>
                  <th className="text-right py-2 px-3 text-sm font-medium" style={{ color: colors.text.secondary }}>
                    現在在庫
                  </th>
                  <th className="text-right py-2 px-3 text-sm font-medium" style={{ color: colors.text.secondary }}>
                    最適在庫
                  </th>
                  <th className="text-right py-2 px-3 text-sm font-medium" style={{ color: colors.text.secondary }}>
                    調整量
                  </th>
                  <th className="text-left py-2 px-3 text-sm font-medium" style={{ color: colors.text.secondary }}>
                    理由
                  </th>
                  <th className="text-center py-2 px-3 text-sm font-medium" style={{ color: colors.text.secondary }}>
                    影響度
                  </th>
                  <th className="text-right py-2 px-3 text-sm font-medium" style={{ color: colors.text.secondary }}>
                    削減額
                  </th>
                  <th className="text-center py-2 px-3 text-sm font-medium" style={{ color: colors.text.secondary }}>
                    アクション
                  </th>
                </tr>
              </thead>
              <tbody>
                {optimizationResults.map((result) => (
                  <tr 
                    key={result.skuId}
                    className="border-b hover:bg-gray-50 transition-colors"
                    style={{ borderColor: colors.ui.border }}
                  >
                    <td className="py-3 px-3 text-sm font-medium" style={{ color: colors.text.primary }}>
                      {result.skuId}
                    </td>
                    <td className="py-3 px-3 text-sm" style={{ color: colors.text.primary }}>
                      {result.skuName}
                    </td>
                    <td className="py-3 px-3 text-sm text-right" style={{ color: colors.text.primary }}>
                      {result.currentStock}
                    </td>
                    <td className="py-3 px-3 text-sm text-right" style={{ color: colors.text.primary }}>
                      {result.optimalStock}
                    </td>
                    <td className="py-3 px-3 text-sm text-right font-medium" style={{ 
                      color: result.adjustment > 0 ? colors.accent.green : result.adjustment < 0 ? colors.accent.red : colors.text.secondary 
                    }}>
                      {result.adjustment > 0 ? '+' : ''}{result.adjustment}品
                    </td>
                    <td className="py-3 px-3 text-xs" style={{ color: colors.text.secondary }}>
                      {result.reason}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <Badge 
                        variant={result.impact === 'high' ? 'error' : result.impact === 'medium' ? 'warning' : 'secondary'}
                        size="sm"
                      >
                        {result.impact === 'high' ? '高' : result.impact === 'medium' ? '中' : '低'}
                      </Badge>
                    </td>
                    <td className="py-3 px-3 text-sm text-right font-medium" style={{ 
                      color: result.savings > 0 ? colors.accent.green : result.savings < 0 ? colors.accent.red : colors.text.secondary 
                    }}>
                      {result.savings > 0 ? '+' : ''}¥{Math.abs(result.savings).toLocaleString()}
                    </td>
                    <td className="py-3 px-3 text-center">
                      {result.adjustment !== 0 ? (
                        <CheckCircle size={16} className="cursor-pointer mx-auto" style={{ color: colors.accent.green }} />
                      ) : (
                        <span className="text-xs" style={{ color: colors.text.secondary }}>-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* サマリー */}
        <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: colors.ui.bgSecondary }}>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: colors.accent.blue }}>
                {optimizationResults.filter(r => r.adjustment !== 0).length}
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                最適化対象SKU
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: colors.accent.green }}>
                ¥{optimizationResults.reduce((sum, r) => sum + Math.max(0, r.savings), 0).toLocaleString()}
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                削減可能額
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: colors.accent.purple }}>
                {optimizationResults.filter(r => r.adjustment < 0).length}
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                在庫削減SKU
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: colors.accent.orange }}>
                {optimizationResults.filter(r => r.adjustment > 0).length}
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                在庫増加SKU
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default OptimizationEngine;
